import React from "react";
import axios from "axios";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Label,
  Button,
  Alert,
  Badge,
  Input
} from "reactstrap";
import moment from "moment";
import { Enviroments } from "../../enviroments/enviroments";
import { calculateStatistics } from "../../helpers/statistics";

/* -------------------- UI Helpers -------------------- */

const COLORS = {
  bg: "#0B0B0F",
  surface: "#121317",
  border: "#26272B",
  text: "#ECECF3",
  sub: "#A3A6AF",
  primary: "#913ef8",
  pink: "#e14eca",
  yellow: "#F6E05E",
  low: "#8B8E98",
  blue: "rgb(52, 180, 255)"
};

const Shimmer = ({ width = "100%", height = 18, radius = 6, className = "" }) => (
  <div
    className={className}
    style={{
      width,
      height,
      borderRadius: radius,
      background:
        "linear-gradient(90deg, rgba(255,255,255,0.06) 25%, rgba(255,255,255,0.12) 37%, rgba(255,255,255,0.06) 63%)",
      backgroundSize: "400% 100%",
      animation: "shimmer 1.2s ease-in-out infinite"
    }}
  />
);

// estilos do shimmer (uma única vez)
const GlobalStyles = () => (
  <style>{`
    @keyframes shimmer {
      0% { background-position: 100% 0; }
      100% { background-position: -100% 0; }
    }
    .chip-scroll { overflow-x: auto; overflow-y: hidden; -webkit-overflow-scrolling: touch; flex-wrap: wrap; }
  `}</style>
);

const StatTile = ({ title, value, upWhenAbove2 = true, loading }) => {
  const up = Number(value) > 2;
  return (
    <Card className="card-chart" style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
      <CardHeader>
        <h6 className="card-category mb-2" style={{ color: COLORS.sub }}>{title}</h6>
        {loading ? (
          <Shimmer width={120} height={28} />
        ) : (
          <CardTitle tag="h3" className="mb-0" style={{ color: COLORS.text }}>
            {value ?? "N/A"}{" "}
            <i
              className={`tim-icons ${upWhenAbove2
                ? (up ? "icon-minimal-up text-success" : "icon-minimal-down text-danger")
                : (!up ? "icon-minimal-up text-success" : "icon-minimal-down text-danger")
              }`}
            />
          </CardTitle>
        )}
      </CardHeader>
    </Card>
  );
};

// cor dos chips por faixa
const chipStyle = (odd) => {
  const v = parseFloat(odd);
  if (Number.isNaN(v)) return { bg: COLORS.surface, text: COLORS.sub, border: COLORS.border };
  if (v >= 10) return { bg: "rgba(225,78,202,0.16)", text: COLORS.pink, border: COLORS.pink };
  if (v >= 2) return { bg: "rgba(145,62,248,0.14)", text: COLORS.primary, border: COLORS.primary };
  if (v < 2) return { bg: "#1b1c21", text: COLORS.blue, border: COLORS.blue };
  return { bg: "#16171b", text: COLORS.low, border: COLORS.border };
};

/* -------------------- Componente -------------------- */

function HistoryOdds({ props }) {
  const dateNow = React.useMemo(() => moment(new Date()).format("YYYY-MM-DD"), []);
  const [history, setHistory] = React.useState([]);
  const [selectedOdds, setSelectedOdds] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [errorMsg, setErrorMsg] = React.useState("");
  const [qtdVelas, setQtdVelas] = React.useState(100);
  const [updatedAt, setUpdatedAt] = React.useState(null);

  // estatísticas
  const [median, setMedian] = React.useState(0);
  const [average, setAverage] = React.useState(0);
  const [stats, setStats] = React.useState({});
  const [limits, setLimits] = React.useState({ limit30: "N/A", limit50: "N/A", limit100: "N/A" });

  const sortChronologically = React.useCallback((arr) => {
    // ordena por data/hora decrescente (mais recente -> mais antigo)
    return [...arr].sort((a, b) => {
      const A = a.date || `${dateNow} ${a.hour}`;
      const B = b.date || `${dateNow} ${b.hour}`;
      return new Date(B).getTime() - new Date(A).getTime();
    });
  }, [dateNow]);

  const getHistory = React.useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const headers = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("internalToken") || ""}`,
          User: `User ${localStorage.getItem("user") || ""}`
        }
      };
      const url = `${Enviroments.API_URL_NODE}history-odd?date=${dateNow}&numberVelas=${qtdVelas}&betHouse=${props.betHouse}`;
      const { data } = await axios.get(url, headers);

      const sorted = sortChronologically(data || []);
      setHistory(sorted);

      // cálculos
      const avg =
        sorted.length
          ? sorted.reduce((acc, it) => acc + parseFloat(it.odd || 0), 0) / sorted.length
          : 0;

      const med = (() => {
        if (!sorted.length) return 0;
        const arr = [...sorted].sort((a, b) => parseFloat(a.odd) - parseFloat(b.odd));
        const mid = Math.floor(arr.length / 2);
        if (arr.length % 2 === 0) {
          return (parseFloat(arr[mid - 1].odd) + parseFloat(arr[mid].odd)) / 2;
        }
        return parseFloat(arr[mid].odd);
      })();

      const s = calculateStatistics(sorted); // {mode, range,...} de acordo com seu helper

      const handleLimit = (list, thr) => {
        // posição do primeiro acima do threshold (no array já cronológico)
        const idx = list.findIndex((x) => parseFloat(x.odd) > thr);
        return idx === -1 ? "N/A" : idx;
      };

      setAverage(Number.isFinite(avg) ? avg : 0);
      setMedian(Number.isFinite(med) ? med : 0);
      setStats(s || {});
      setLimits({
        limit30: handleLimit(sorted, 30),
        limit50: handleLimit(sorted, 50),
        limit100: handleLimit(sorted, 100)
      });
      setUpdatedAt(moment().format("HH:mm:ss"));
    } catch (e) {
      console.error(e);
      setErrorMsg("Não foi possível carregar o histórico agora. Tente novamente em instantes.");
    } finally {
      setLoading(false);
    }
  }, [dateNow, qtdVelas, props?.betHouse, sortChronologically]);

  React.useEffect(() => {
    getHistory();
  }, [getHistory]);

  const toggleSelectOdd = (odd) => {
    setSelectedOdds((prev) =>
      prev.includes(odd) ? prev.filter((o) => o !== odd) : [...prev, odd]
    );
  };

  /* -------------------- Render -------------------- */

  const selectedBorder = (odd) =>
    selectedOdds.includes(odd) ? "2px solid #00FF7F" : "1px solid transparent";

  return (
    <>
      <GlobalStyles />

      {/* Título + filtros */}
      <Row>
        <Col xs="12">
          <Card style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
            <CardHeader className="pb-2">
              <Row className="align-items-end">
                <Col md="6" className="mb-2 mb-md-0">
                  <h5 className="card-category" style={{ color: COLORS.sub }}>
                    Histórico de velas do Aviator em tempo real
                  </h5>
                  <div className="d-flex align-items-center">
                    <CardTitle tag="h3" className="mb-0" style={{ color: COLORS.text }}>
                      Histórico de Velas
                    </CardTitle>
                    <Button
                      size="sm"
                      className="ml-3"
                      color="primary"
                      style={{ background: COLORS.primary, border: "none" }}
                      onClick={getHistory}
                      disabled={loading}
                    >
                      {loading ? "Atualizando..." : "Atualizar"}
                    </Button>
                  </div>
                  {updatedAt && (
                    <div style={{ color: COLORS.sub, fontSize: 12, marginTop: 6 }}>
                      Última atualização: <strong style={{ color: COLORS.text }}>{updatedAt}</strong>
                    </div>
                  )}
                </Col>

                <Col md="6" className="d-flex justify-content-md-end">
                  <div>
                    <Label for="selectVelas" style={{ color: COLORS.sub, fontSize: 12 }}>
                      Qtd. de velas
                    </Label>
                    <Input
                      id="selectVelas"
                      type="select"
                      value={qtdVelas}
                      onChange={(e) => setQtdVelas(Number(e.target.value))}
                      style={{
                        width: 180,
                        background: COLORS.bg,
                        color: COLORS.text,
                        border: `1px solid ${COLORS.border}`
                      }}
                    >
                      <option value="10">10</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                      <option value="200">200</option>
                      <option value="500">500</option>
                      <option value="1000">1000</option>
                    </Input>
                  </div>
                </Col>
              </Row>
            </CardHeader>

            <CardBody>
              {errorMsg && (
                <Alert color="danger" style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
                  {errorMsg}
                </Alert>
              )}

              {/* Últimos multiplicadores (antigo → recente) */}
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span style={{ color: COLORS.sub, fontSize: 12 }}>Últimos multiplicadores</span>
                <span style={{ color: COLORS.sub, fontSize: 11 }}>(mais recente ←)</span>
              </div>

              {loading ? (
                <div className="d-flex chip-scroll" style={{ gap: 8 }}>
                  {Array.from({ length: 14 }).map((_, i) => (
                    <Shimmer key={i} width={72} height={34} />
                  ))}
                </div>
              ) : history?.length ? (
                <div className="chip-scroll" style={{ display: "flex", gap: 8, paddingBottom: 6 }}>
                  {history.map((item, idx) => {
                    const style = chipStyle(item.odd);
                    return (
                      <div
                        key={item.id ?? `${item.hour}-${idx}`}
                        onClick={() => toggleSelectOdd(item.odd)}
                        title={`${item.hour} • ${Number(item.odd).toFixed(2)}x`}
                        style={{
                          padding: "6px 10px",
                          borderRadius: 14,
                          border: selectedBorder(item.odd),
                          background: style.bg,
                          color: style.text,
                          fontWeight: 700,
                          minWidth: 64,
                          textAlign: "center",
                          whiteSpace: "nowrap",
                          fontSize: 13,
                          cursor: "pointer"
                        }}
                      >
                        {Number(item.odd).toFixed(2)}x
                        <div style={{ fontSize: 11, color: COLORS.sub }}>{item.hour}</div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ color: COLORS.sub, fontSize: 13 }}>Nenhum dado disponível.</div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Estatísticas */}
      <Row>
        <Col xs="6" sm="3" lg="2">
          <StatTile
            title={`Mediana • filtrada por ${qtdVelas}`}
            value={loading ? null : Number(median).toFixed(2)}
            loading={loading}
          />
        </Col>
        <Col xs="6" sm="3" lg="2">
          <StatTile
            title={`Média • filtrada por ${qtdVelas}`}
            value={loading ? null : Number(average).toFixed(2)}
            loading={loading}
          />
        </Col>
        <Col xs="6" sm="3" lg="2">
          <Card className="card-chart" style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
            <CardHeader>
              <h6 className="card-category mb-2" style={{ color: COLORS.sub }}>
                Moda • filtrada por {qtdVelas}
              </h6>
              {loading ? (
                <Shimmer width={160} height={28} />
              ) : (
                <CardTitle tag="h3" className="mb-0" style={{ color: COLORS.text }}>
                  {stats && stats.mode !== undefined
                    ? Array.isArray(stats.mode)
                      ? stats.mode.join(", ")
                      : stats.mode
                    : "N/A"}{" "}
                  <i
                    className={`tim-icons ${
                      Number(stats?.mode) > 2
                        ? "icon-minimal-up text-success"
                        : "icon-minimal-down text-danger"
                    }`}
                  />
                </CardTitle>
              )}
            </CardHeader>
          </Card>
        </Col>
        <Col xs="6" sm="3" lg="2">
          <Card className="card-chart" style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
            <CardHeader>
              <h6 className="card-category mb-2" style={{ color: COLORS.sub }}>
                Amplitude • filtrada por {qtdVelas}
              </h6>
              {loading ? (
                <Shimmer width={120} height={28} />
              ) : (
                <CardTitle tag="h3" className="mb-0" style={{ color: COLORS.text }}>
                  {stats && stats.range !== undefined ? Number(stats.range).toFixed(2) : "N/A"}{" "}
                  <i
                    className={`tim-icons ${
                      Number(stats?.range) > 2
                        ? "icon-minimal-up text-success"
                        : "icon-minimal-down text-danger"
                    }`}
                  />
                </CardTitle>
              )}
            </CardHeader>
          </Card>
        </Col>
      </Row>

      {/* Limites */}
      <Row>
        <Col xs="12" lg="4">
          <Card className="card-chart" style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
            <CardHeader>
              <h6 className="card-category mb-2" style={{ color: COLORS.sub }}>
                Limites (posição do primeiro acima do limiar)
              </h6>
              {loading ? (
                <>
                  <Shimmer width={240} height={16} className="mb-2" />
                  <Shimmer width={240} height={16} className="mb-2" />
                  <Shimmer width={240} height={16} />
                </>
              ) : (
                <>
                  <p className="card-category mb-1" style={{ color: COLORS.sub }}>
                    Última <Badge color="danger">30x</Badge> – Posição:
                    <strong className="ml-1 text-primary" style={{ fontSize: 14 }}>
                      {limits?.limit30}
                    </strong>
                  </p>
                  <p className="card-category mb-1" style={{ color: COLORS.sub }}>
                    Última <Badge color="warning">50x</Badge> – Posição:
                    <strong className="ml-1 text-primary" style={{ fontSize: 14 }}>
                      {limits?.limit50}
                    </strong>
                  </p>
                  <p className="card-category mb-0" style={{ color: COLORS.sub }}>
                    Última <Badge color="info">100x</Badge> – Posição:
                    <strong className="ml-1 text-primary" style={{ fontSize: 14 }}>
                      {limits?.limit100}
                    </strong>
                  </p>
                </>
              )}
            </CardHeader>
          </Card>
        </Col>
      </Row>
    </>
  );
}

export default HistoryOdds;
